import { LeftOutlined } from '@ant-design/icons';
import { CAPABILITY_VERSION } from '@healthhub/message-channel';
import { Notification, twClassMerge  } from '@healthhub/react-ui';
import { Typography } from 'antd';
import type { PropsWithChildren, ReactNode } from 'react';
import { memo } from 'react';

import { OutlinedBellIcon } from '@shared/assets-icon';
import type { CustomBreadcrumbProps } from '@shared/components/CustomBreadcrumb';
import { CustomBreadcrumb } from '@shared/components/CustomBreadcrumb';
import { useLayoutViewContext } from '@shared/context';


import MaxWidthContainer from './portal/MaxWidthContainer';

const { Text, Title } = Typography;

export interface ContentLayoutProps {
  backButton?: {
    name: string;
    onBack: () => void;
  };
  breadcrumbs?: {
    portal?: CustomBreadcrumbProps;
    standard?: Pick<CustomBreadcrumbProps, 'breadcrumbsDetails'>;
  };
  breadcrumbsComponent?: {
    portal?: ReactNode;
    standard?: ReactNode;
  };
  className?: string;
  description?: string;
  importantNotices?: ImportantNoticeProps;
  title?: ReactNode;
}

export interface ImportantNoticeProps {
  message: string;
  title: string;
}

const ContentLayout = ({
  backButton,
  breadcrumbs,
  breadcrumbsComponent,
  children,
  className,
  description,
  title,
  importantNotices,
}: PropsWithChildren<ContentLayoutProps>) => {
  const { capabilityVersion, isPortalView, isStandardView } = useLayoutViewContext();

  return (
    <div
      className={twClassMerge(
        'flex grow h-full flex-col w-full',
        { 'gap-4 md:gap-6': isPortalView },
        { 'gap-4 px-4 py-5': isStandardView },
        className,
        {
          'md:gap-10': !!backButton,
        }
      )}
    >
      {!!isPortalView && !!((breadcrumbs?.portal?.breadcrumbsDetails?.length ?? 0) > 0 || title) && (
        <div className="flex flex-col gap-2">
          {breadcrumbsComponent?.portal ||
            (breadcrumbs?.portal?.breadcrumbsDetails && (
              <CustomBreadcrumb
                breadcrumbsDetails={breadcrumbs.portal.breadcrumbsDetails}
                isIncludedDefaultNav={breadcrumbs.portal?.isIncludedDefaultNav}
              />
            ))}
          {!!title && (
            <MaxWidthContainer className="flex">
              <div className="flex flex-col gap-2">
                <Title className="m-0 text-3xl lg:text-4.5xl lg:leading-tight" level={1}>
                  {title}
                </Title>
                {!!description && <Text className="text-sm">{description}</Text>}
              </div>
            </MaxWidthContainer>
          )}
        </div>
      )}
      {!!isStandardView &&
        capabilityVersion === CAPABILITY_VERSION.C2 &&
        (breadcrumbsComponent?.standard
          ? breadcrumbsComponent?.standard
          : breadcrumbs?.standard && (
              <CustomBreadcrumb
                breadcrumbsDetails={breadcrumbs.standard.breadcrumbsDetails}
                isIncludedDefaultNav={false}
              />
            ))}
      <div className="flex h-full grow flex-col gap-4">
        {!!backButton && !!isPortalView && (
          <button
            className="flex items-center gap-2"
            data-testid="back-button"
            onClick={() => backButton.onBack()}
            type="button"
          >
            <LeftOutlined />
            <h2 className="mb-[-0.01rem] text-sm font-semibold">{backButton.name}</h2>
          </button>
        )}
        {importantNotices?.message ? (
          <div className="flex flex-col" lang="en">
            <Notification
              customIcon={<OutlinedBellIcon className="[&_path]:stroke-theme-700 size-4px" />}
              customTitle={importantNotices?.title}
              message={importantNotices?.message}
              variant="insight"
            />
            <div className="flex grow flex-col gap-4">{children}</div>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

export default memo(ContentLayout);
